/**
 * AuditLogPanel — last 200 audited admin actions with kind + actor filters
 * and CSV export of the current view.
 */
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Download, RefreshCw } from "lucide-react";

interface Row {
  id: string;
  actor_id: string;
  target_id: string | null;
  kind: string;
  payload: Record<string, unknown>;
  reason: string | null;
  created_at: string;
}

const ALL_KIND = "__all__";

export const AuditLogPanel = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState<string>(ALL_KIND);
  const [search, setSearch] = useState("");

  const reload = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("admin_actions")
      .select("id, actor_id, target_id, kind, payload, reason, created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    setRows((data as Row[] | null) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    void reload();
  }, []);

  const kinds = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => s.add(r.kind));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (kindFilter !== ALL_KIND && r.kind !== kindFilter) return false;
      if (!q) return true;
      return (
        r.actor_id.toLowerCase().includes(q) ||
        (r.target_id ?? "").toLowerCase().includes(q) ||
        (r.reason ?? "").toLowerCase().includes(q) ||
        r.kind.toLowerCase().includes(q)
      );
    });
  }, [rows, kindFilter, search]);

  const exportCsv = () => {
    const header = ["created_at", "kind", "actor_id", "target_id", "reason", "payload"];
    const escape = (v: unknown) =>
      `"${String(v ?? "").replace(/"/g, '""').replace(/\n/g, " ")}"`;
    const body = filtered
      .map((r) =>
        [r.created_at, r.kind, r.actor_id, r.target_id ?? "", r.reason ?? "", JSON.stringify(r.payload ?? {})]
          .map(escape)
          .join(","),
      )
      .join("\n");
    const blob = new Blob([header.join(",") + "\n" + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wavlynq-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground block mb-1">Search</label>
          <Input
            placeholder="actor, target, reason, kind…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Kind</label>
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_KIND}>All kinds</SelectItem>
              {kinds.map((k) => (
                <SelectItem key={k} value={k}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={filtered.length === 0}>
          <Download className="h-4 w-4" /> CSV
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Showing {filtered.length} of {rows.length} most recent actions.
      </p>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No matching admin actions.</p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((r) => (
            <li key={r.id}>
              <Card className="p-3">
                <div className="flex items-center justify-between gap-3">
                  <Badge variant="secondary">{r.kind}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-xs font-mono text-muted-foreground mt-2 space-y-0.5">
                  <div>actor: {r.actor_id.slice(0, 8)}…</div>
                  {r.target_id && <div>target: {r.target_id.slice(0, 8)}…</div>}
                  {r.reason && <div>reason: {r.reason}</div>}
                  {Object.keys(r.payload ?? {}).length > 0 && (
                    <pre className="mt-1 p-2 rounded bg-muted/40 overflow-x-auto">
                      {JSON.stringify(r.payload, null, 2)}
                    </pre>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AuditLogPanel;
